import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import store from "@/store";
import TokenService from "@/api.resource/token.service";
import { canAccessWorkspaceApp, getNextAccountSetupRoute, getPendingSetupSteps } from "@/lib/utils";

// Lazy-loaded views

// Auth
const SignUp = () => import(/* webpackChunkName: "auth-sign-up" */ "@/views/SignUp.vue");
const Terms = () => import(/* webpackChunkName: "auth-terms" */ "@/views/Terms.vue");
const Login = () => import(/* webpackChunkName: "auth-login" */ "@/views/Login.vue");
const MfaAuthentication = () => import(/* webpackChunkName: "auth-mfa" */ "@/views/MfaAuthentication.vue");
const ForgotPassword = () => import(/* webpackChunkName: "auth-forgot-password" */ "@/views/ForgotPassword.vue");
const ResetPassword = () => import(/* webpackChunkName: "auth-reset-password" */ "@/views/ResetPassword.vue");

// Layouts
const DashboardLayout = () => import(/* webpackChunkName: "layout-dashboard" */ "@/components/layouts/DashboardLayout.vue");
const SettingsLayout = () => import(/* webpackChunkName: "layout-settings" */ "@/components/layouts/SettingsLayout.vue");

// Dashboard views
const Dashboard = () => import(/* webpackChunkName: "dashboard-home" */ "@/views/dashboard/Index.vue");
const ManagePlan = () => import(/* webpackChunkName: "dashboard-manage-plan" */ "@/views/dashboard/ManagePlan.vue");
const ManageBranch = () => import(/* webpackChunkName: "dashboard-manage-branch" */ "@/views/dashboard/ManageBranch.vue");
const ManageStaff = () => import(/* webpackChunkName: "dashboard-manage-staff" */ "@/views/dashboard/ManageStaff.vue");
const ManageGroups = () => import(/* webpackChunkName: "dashboard-manage-groups" */ "@/views/dashboard/ManageGroups.vue");
const BillingInfo = () => import(/* webpackChunkName: "dashboard-billing-info" */ "@/views/dashboard/BillingInfo.vue");
const StaffVerification = () => import(/* webpackChunkName: "dashboard-staff-verification" */ "@/views/dashboard/StaffVerification.vue");

// Settings views
const EditProfile = () => import(/* webpackChunkName: "settings-edit-profile" */ "@/views/dashboard/settings/EditProfile.vue");
const ChangePassword = () => import(/* webpackChunkName: "settings-change-password" */ "@/views/dashboard/settings/ChangePassword.vue");
const TwoFAAuthentication = () => import(/* webpackChunkName: "settings-authentication" */ "@/views/dashboard/settings/Authentication.vue");
const Preferences = () => import(/* webpackChunkName: "settings-preferences" */ "@/views/dashboard/settings/Preferences.vue");

// Account Setup views
const MedicalCenterData = () => import(/* webpackChunkName: "setup-medical-center-data" */ "@/views/account-setup/MedicalCenterData.vue");
const OwnerLicensedInfo = () => import(/* webpackChunkName: "setup-owner-licensed-info" */ "@/views/account-setup/OwnerLicensedInfo.vue");
const MedicalCenterDocs = () => import(/* webpackChunkName: "setup-medical-center-docs" */ "@/views/account-setup/MedicalCenterDocs.vue");

const routes: RouteRecordRaw[] = [
  // Auth routes
  { path: "/register", name: "SignUp", component: SignUp },
  { path: "/terms", name: "Terms", component: Terms },
  { path: "/login", name: "Login", component: Login },
  { path: "/mfa-authentication", name: "MfaAuthentication", component: MfaAuthentication },
  { path: "/forgot-password", name: "ForgotPassword", component: ForgotPassword },
  { path: "/reset-password", name: "ResetPassword", component: ResetPassword },

  // Account setup routes
  { path: "/account/medical-center-data", name: "MedicalCenterData", component: MedicalCenterData },
  { path: "/account/owner-licensed-info", name: "OwnerLicensedInfo", component: OwnerLicensedInfo },
  { path: "/account/medical-center-docs", name: "MedicalCenterDocs", component: MedicalCenterDocs },

  // Dashboard layout & nested routes
  {
    path: "/",
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      { path: "", name: "Dashboard", component: Dashboard },
      { path: "manage-plan", name: "ManagePlan", component: ManagePlan },
      { path: "manage-branch", name: "ManageBranch", component: ManageBranch },
      { path: "manage-staff", name: "ManageStaff", component: ManageStaff },
      { path: "manage-groups", name: "ManageGroups", component: ManageGroups },
      { path: "billing-info", name: "BillingInfo", component: BillingInfo },
      { path: "staff-verification", name: "StaffVerification", component: StaffVerification },

      // Settings layout & nested routes
      {
        path: "settings",
        name: "Settings",
        component: SettingsLayout,
        children: [
          { path: "edit-profile", name: "EditProfile", component: EditProfile },
          { path: "change-password", name: "ChangePassword", component: ChangePassword },
          { path: "2fa-authentication", name: "2FA-Authentication", component: TwoFAAuthentication },
          { path: "preferences", name: "Preferences", component: Preferences },
        ],
      },
    ],
  },

  // Fallback
  { path: "/:pathMatch(.*)*", redirect: "/login" },
];

export const PUBLIC_AUTH_ROUTE_NAMES = [
  "SignUp",
  "Terms",
  "Login",
  "MfaAuthentication",
  "ForgotPassword",
  "ResetPassword"
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return Promise.resolve({ left: 0, top: 0 });
  }
});

router.beforeEach((to, from, next) => {
  const token = TokenService.getAccessToken();
  const sessionToken = sessionStorage.getItem("sessionToken");

  const mcAccountStatus = store.getters["account/mcAccountStatus"];
  const setupStatus = mcAccountStatus?.MCStatus;

  const isPublicAuthRoutes = PUBLIC_AUTH_ROUTE_NAMES.includes(to.name as string);

  if (isPublicAuthRoutes) {
    next();
    return;
  }

  if (!token) {
    next({ name: "Login" });
    return;
  }

  if (to.name === "MfaAuthentication" && !sessionToken) {
    next({ name: "Login" });
    return;
  }

  const isAccountSetupRoute = ["MedicalCenterData", "OwnerLicensedInfo", "MedicalCenterDocs"].includes(to.name as string);

  const setupRouteByStep: Record<string, string> = {
    "": "MedicalCenterData",
    "/account/medical-center-data": "MedicalCenterData",
    "/account/owner-licensed-info": "OwnerLicensedInfo",
    "/account/medical-center-docs": "MedicalCenterDocs",
  };
  const nextSetupRoute = getNextAccountSetupRoute(setupStatus);
  const expectedSetupRouteName = setupRouteByStep[nextSetupRoute] ?? "MedicalCenterData";

  const canAccessWorkspace = canAccessWorkspaceApp(setupStatus);
  const pendingSetupSteps = getPendingSetupSteps(setupStatus);

  const setupRouteOrder: Record<string, number> = {
    MedicalCenterData: 1,
    OwnerLicensedInfo: 2,
    MedicalCenterDocs: 3,
  };

  // Step 1/workspace-ready is enough to let the user into the app.
  // Pending steps are still surfaced in the UI, but they should not hard-block
  // plan selection or other workspace actions after partial onboarding.
  if (!canAccessWorkspace && !isAccountSetupRoute) {
    next({ name: expectedSetupRouteName });
    return;
  }

  // Allow skipping forward through setup steps; only block backwards navigation
  // past the first outstanding step (which would let users bypass step 1).
  if (pendingSetupSteps.length && isAccountSetupRoute) {
    const toOrder = setupRouteOrder[to.name as string] ?? 0;
    const expectedOrder = setupRouteOrder[expectedSetupRouteName] ?? 0;
    if (toOrder < expectedOrder) {
      next({ name: expectedSetupRouteName });
      return;
    }
  }

  if (!pendingSetupSteps.length && isAccountSetupRoute) {
    const isFromValidAppRoute =
      from.name &&
      !PUBLIC_AUTH_ROUTE_NAMES.includes(from.name as string) &&
      !["MedicalCenterData", "OwnerLicensedInfo", "MedicalCenterDocs"].includes(from.name as string);

    next(isFromValidAppRoute ? { name: from.name as string } : { name: "Dashboard" });
    return;
  }

  next();
});

export default router;
